import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db, SUBSCRIPTION_TIERS, CREDIT_COSTS, normalizeSubscriptionTier } from '../services/firebase';
import { useAuth } from './AuthContext';
import { SubscriptionTier } from '../types';
import { reportError } from '../services/errorReporting';

interface CreditsContextType {
  credits: number;
  maxCredits: number;
  subscription: SubscriptionTier;
  loading: boolean;
  hasEnoughCredits: (amount: number) => boolean;
  deductCredits: (amount: number) => Promise<boolean>;
  refreshCredits: () => Promise<void>;
  CREDIT_COSTS: typeof CREDIT_COSTS;
}

const CreditsContext = createContext<CreditsContextType | null>(null);

export const useCredits = () => {
  const context = useContext(CreditsContext);
  if (!context) {
    throw new Error('useCredits must be used within a CreditsProvider');
  }
  return context;
};

interface CreditsProviderProps {
  children: ReactNode;
}

export const CreditsProvider: React.FC<CreditsProviderProps> = ({ children }) => {
  const { user, userData } = useAuth();
  const [credits, setCredits] = useState(0);
  const [subscription, setSubscription] = useState<SubscriptionTier>('free');
  const [loading, setLoading] = useState(true);

  const maxCredits = SUBSCRIPTION_TIERS[subscription].monthlyCredits;

  // Refresh credits from Firestore (read-only — credits are now only modified by Cloud Functions)
  const refreshCredits = useCallback(async () => {
    if (!user) {
      setCredits(0);
      setSubscription('free');
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const userRef = doc(db, 'users', user.uid);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists()) {
        const data = userSnap.data();
        setCredits(data.credits ?? 0);
        setSubscription(normalizeSubscriptionTier(data.subscription));
      } else if (userData) {
        setCredits(userData.credits);
        setSubscription(userData.subscription);
      }
    } catch (error) {
      reportError(error, { module: 'credits', extra: { uid: user?.uid } });
    } finally {
      setLoading(false);
    }
  }, [user, userData]);

  // Load credits when user changes
  useEffect(() => {
    refreshCredits();
  }, [refreshCredits]);

  // Check if user has enough credits (local check for UI gating — server is the authority)
  const hasEnoughCredits = useCallback((amount: number): boolean => {
    return credits >= amount;
  }, [credits]);

  // deductCredits is now a no-op: credits are deducted server-side by Cloud Functions.
  // After calling a callable, components should call refreshCredits() to sync the UI.
  const deductCredits = useCallback(async (_amount: number): Promise<boolean> => {
    // Server-side deduction happens in Cloud Functions; just refresh to get the new balance
    await refreshCredits();
    return true;
  }, [refreshCredits]);

  const value: CreditsContextType = {
    credits,
    maxCredits,
    subscription,
    loading,
    hasEnoughCredits,
    deductCredits,
    refreshCredits,
    CREDIT_COSTS
  };

  return (
    <CreditsContext.Provider value={value}>
      {children}
    </CreditsContext.Provider>
  );
};
